import React, { useEffect, useRef, useState } from 'react';
import {
  FaArrowRight, FaArrowLeft, FaArrowUp, FaBrain, FaBuilding, FaChartLine, FaCloud, FaCode,
  FaCube, FaDatabase, FaGamepad, FaCogs, FaLayerGroup, FaMobileAlt,
  FaPalette, FaPlay, FaRobot, FaVideo, FaTimes, FaPaperPlane, FaBars,
  FaFacebookF, FaInstagram, FaLinkedinIn, FaTwitter, FaYoutube, FaComments, FaChevronDown
} from 'react-icons/fa';
import { SiTiktok } from 'react-icons/si';
import './_Styles/LoopyLandingModule.css';
import HERO_IMAGE from './_Assets/loopy-team-hero-home.jpg';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { BrandLS } from '../_Layouts/_Footers/_BrandLS';
import { useNoAutofill } from '../../../_CORE/Hooks/useNoAutofill';
import { CONTACT_FORM_LIMITS, normalizeContactText, validateContactEmail, validateContactMessage, validateContactName } from './_Security/ContactFormSecurity';

const SERVICE_ICONS = {
  code: FaCode, cloud: FaCloud, mobile: FaMobileAlt, brain: FaBrain, robot: FaRobot,
  chart: FaChartLine, database: FaDatabase, palette: FaPalette, layers: FaLayerGroup,
  cube: FaCube, video: FaVideo, gamepad: FaGamepad, play: FaPlay, cogs: FaCogs,
};

const RUNTIME_BRAND = (typeof window !== 'undefined' && window.__APP_CONFIG__) ? window.__APP_CONFIG__ : {};
const LANDING_LOGO = RUNTIME_BRAND.APP_LOGO_DARK || '/_Brand/Logo/logo-dark.png';
const LANDING_BRAND_NAME = RUNTIME_BRAND.COMPANY_NAME || RUNTIME_BRAND.APP_NAME || 'Loopy Studios';

const LANDING_SOCIALS = [
  { key: 'facebook', label: 'Facebook', url: RUNTIME_BRAND.COMPANY_FACEBOOK, Icon: FaFacebookF },
  { key: 'instagram', label: 'Instagram', url: RUNTIME_BRAND.COMPANY_INSTAGRAM, Icon: FaInstagram },
  { key: 'linkedin', label: 'LinkedIn', url: RUNTIME_BRAND.COMPANY_LINKEDIN, Icon: FaLinkedinIn },
  { key: 'twitter', label: 'X / Twitter', url: RUNTIME_BRAND.COMPANY_TWITTER, Icon: FaTwitter },
  // { key: 'tiktok', label: 'TikTok', url: RUNTIME_BRAND.COMPANY_TIKTOK, Icon: SiTiktok },
  // { key: 'youtube', label: 'YouTube', url: RUNTIME_BRAND.COMPANY_YOUTUBE, Icon: FaYoutube },
];

const CATEGORY_LABEL = {
  'WEB APP': 'Web', 'MOBILE APP': 'Móvil', '3D & ASSETS': '3D & Assets',
  SOUNDTRACK: 'Sonido', IA: 'Otros', WEB: 'Web', MOBILE: 'Móvil'
};

const resolveDbAsset = (assetPath) => {
  if (!assetPath) return '';
  if (/^(https?:|data:|blob:)/i.test(assetPath)) return assetPath;
  const normalized = String(assetPath).replace(/\\/g, '/').replace(/^\.?\/?/, '').replace(/^DB_LOCAL\//i, '');
  return `/DB_LOCAL/${normalized}`;
};


const getExternalUrl = (item) => {
  const value = item?.url || item?.website || item?.link || '';
  if (!value) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const openExternalUrl = (item) => {
  const url = getExternalUrl(item);
  if (!url || typeof window === 'undefined') return;
  window.open(url, '_blank', 'noopener,noreferrer');
};

const HERO_DOTS = [
  { top: 12, left: 18, depth: .3, size: 3 }, { top: 8, left: 42, depth: .55, size: 2 },
  { top: 22, left: 78, depth: .4, size: 3 }, { top: 38, left: 6, depth: .65, size: 2 },
  { top: 70, left: 14, depth: .35, size: 2 }, { top: 85, left: 36, depth: .5, size: 3 },
  { top: 90, left: 70, depth: .3, size: 2 }, { top: 65, left: 90, depth: .6, size: 3 },
  { top: 30, left: 94, depth: .45, size: 2 },
];
const HERO_LINES = [[0,1],[1,2],[3,0],[4,5],[5,6],[7,8],[8,2]];

const FALLBACK_SERVICES = [
  { title:'Software a Medida & SaaS', icon:'cloud' },
  { title:'Desarrollo Web & Mobile', icon:'mobile' },
  { title:'Agentes IA', icon:'robot' },
  { title:'Inteligencia Artificial', icon:'brain' },
  { title:'Automatización', icon:'cogs' },
];
const FALLBACK_PROJECTS = [];
const FALLBACK_CLIENTS = [];


const LANDING_NAV = [
  ['home', 'Inicio'],
  ['services', 'Servicios'],
  ['clientes', 'Clientes'],
  ['projects', 'Proyectos'],
  ['about', 'Nosotros'],
];

function LandingHeader() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState('home');
  const navigationLockRef = useRef(null);
  const navigationUnlockTimerRef = useRef(null);

  useEffect(() => {
    const unlockNavigation = () => {
      navigationLockRef.current = null;
      if (navigationUnlockTimerRef.current) {
        window.clearTimeout(navigationUnlockTimerRef.current);
        navigationUnlockTimerRef.current = null;
      }
    };

    const onScroll = () => {
      // Durante un scroll iniciado desde el menú conservamos el item elegido.
      // Así no "parpadea" pasando por las secciones intermedias.
      if (navigationLockRef.current) return;

      const offset = 150;
      let current = 'home';
      LANDING_NAV.slice(1).forEach(([id]) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= offset) current = id;
      });
      setActive(current);
    };

    const onScrollEnd = () => {
      if (!navigationLockRef.current) return;
      // Un pequeño margen evita que el último frame del smooth-scroll recalcule otra sección.
      window.setTimeout(unlockNavigation, 60);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scrollend', onScrollEnd, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('scrollend', onScrollEnd);
      if (navigationUnlockTimerRef.current) window.clearTimeout(navigationUnlockTimerRef.current);
    };
  }, []);

  const go = (event, id) => {
    event.preventDefault();
    setOpen(false);

    navigationLockRef.current = id;
    setActive(id);

    if (navigationUnlockTimerRef.current) window.clearTimeout(navigationUnlockTimerRef.current);
    // Fallback para navegadores sin `scrollend`.
    navigationUnlockTimerRef.current = window.setTimeout(() => {
      navigationLockRef.current = null;
      navigationUnlockTimerRef.current = null;
    }, 1400);

    if (id === 'home') window.scrollTo({ top: 0, behavior: 'smooth' });
    else document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return <header className="LoopyLegacyHeader">
    <div className="LoopyLegacyHeader__brandRow">
      <a href="#home" className="LoopyLegacyHeader__brand" onClick={(e)=>go(e,'home')} aria-label="Loopy Studios - Inicio">
        <img className="LoopyLegacyHeader__brandLogo" src={LANDING_LOGO} alt={LANDING_BRAND_NAME} />
      </a>
      <button type="button" className="LoopyLegacyHeader__toggle" aria-label={open ? 'Cerrar menú' : 'Abrir menú'} aria-expanded={open} onClick={()=>setOpen(v=>!v)}>{open ? <FaTimes/> : <FaBars/>}</button>
    </div>
    <div className={`LoopyLegacyHeader__navRow ${open ? 'is-open' : ''}`}>
      <nav className="LoopyLegacyHeader__nav" aria-label="Navegación principal">
        {LANDING_NAV.map(([id,label]) => <a key={id} href={id==='home'?'#home':`#${id}`} className={active===id?'is-active':''} onClick={(e)=>go(e,id)}>{label}</a>)}
      </nav>
    </div>
  </header>;
}

function LandingFooter() {
  const currentYear = new Date().getFullYear();
  return <footer className="LoopyLegacyFooter LoopyLegacyFooter--minimal">
    <div className="LoopyLegacyFooter__socials LoopyLegacyFooter__socials--minimal" aria-label="Redes sociales">
      <span className="LoopyLegacyFooter__socialsTitle">Seguinos</span>
      <div className="LoopyLegacyFooter__socialsList">
        {LANDING_SOCIALS.map(({ key, label, url, Icon }) => {
          const enabled = typeof url === 'string' && url.trim();
          return enabled
            ? <a key={key} href={url} target="_blank" rel="noreferrer" aria-label={label} title={label}><Icon /></a>
            : <span key={key} className="is-disabled" aria-label={`${label} - configurar en MainCompany.js`} title={`${label}: configurá la URL en MainCompany.js`}><Icon /></span>;
        })}
      </div>
    </div>
    <div className="LoopyLegacyFooter__bottom">© {LANDING_BRAND_NAME} · 2025–{currentYear}. Todos los derechos reservados.</div>
    <BrandLS />
  </footer>;
}

function Hero() {
  const ref = useRef(null);
  const frame = useRef(null);
  useEffect(() => {
    const onScroll = () => {
      if (frame.current) return;
      frame.current = requestAnimationFrame(() => {
        const el = ref.current;
        if (el) {
          const rect = el.getBoundingClientRect();
          const progress = Math.min(Math.max(-rect.top / rect.height, 0), 1);
          el.style.setProperty('--scroll', progress.toFixed(3));
        }
        frame.current = null;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true }); onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const mouseMove = (e) => {
    const el = ref.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--px', (((e.clientX-rect.left)/rect.width)-.5).toFixed(3));
    el.style.setProperty('--py', (((e.clientY-rect.top)/rect.height)-.5).toFixed(3));
  };
  return <section className="LStudio__app_container_hero_view" ref={ref} onMouseMove={mouseMove} onMouseLeave={() => { ref.current?.style.setProperty('--px',0); ref.current?.style.setProperty('--py',0); }}>
    <div className="LStudio__app_content_hv_bg_image" aria-hidden="true" style={{ backgroundImage:`url(${HERO_IMAGE})` }} />
    <div className="LStudio__app_content_hv_cinematic_overlay" aria-hidden="true" />
    <div className="LStudio__app_container_hv_aurora_wrap" aria-hidden="true"><div className="LStudio__app_content_hv_aurora" /></div>
    <div className="LStudio__app_container_hv_grid" aria-hidden="true" /><div className="LStudio__app_content_hv_noise" aria-hidden="true" />
    <svg className="LStudio__app_container_hv_constellation" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
      {HERO_LINES.map(([a,b],i)=><line key={i} x1={HERO_DOTS[a].left} y1={HERO_DOTS[a].top} x2={HERO_DOTS[b].left} y2={HERO_DOTS[b].top} className="LStudio__app_content_hv_constellation_line" style={{animationDelay:`${i*.5}s`}} vectorEffect="non-scaling-stroke" />)}
    </svg>
    <div className="LStudio__app_content_hv_dots" aria-hidden="true">{HERO_DOTS.map((dot,i)=><span key={i} className="LStudio__app_content_hv_dot" style={{top:`${dot.top}%`,left:`${dot.left}%`,width:`${dot.size}px`,height:`${dot.size}px`,'--depth':dot.depth}} />)}</div>
    <div className="LStudio__app_container_hv_hero_content">
      <h1 className="LStudio__app_content_hv_hero_title">
        <span className="LStudio__app_content_hv_title_lead">Convertimos tu idea en una</span>
        <span className="LStudio__app_content_hv_title_impact">
          <span className="LStudio__app_content_hv_reveal">SOLUCIÓN</span>
          <span className="LStudio__app_content_hv_title_real"> real</span>
        </span>
      </h1>
      <p className="LStudio__app_content_hv_hero_sub">
        <span className="LStudio__app_content_hv_sub_line">Creamos software a medida y SaaS, desarrollamos soluciones web/mobile</span>
        <span className="LStudio__app_content_hv_sub_line">e integramos agentes de IA y automatizaciones para optimizar procesos, reducir tareas manuales y ayudar a que tu negocio crezca.</span>
      </p>
    </div>
    <button type="button" className="LStudio__app_content_hv_scroll_cta" onClick={()=>document.getElementById('services')?.scrollIntoView({behavior:'smooth'})} aria-label="Desplazarse hacia abajo"><span className="LStudio__app_container_hv_scroll_mouse"><span className="LStudio__app_content_hv_scroll_dot" /></span></button>
  </section>;
}

function Services({ services }) {
  return <section id="services" className="LStudio__app_container_sv_section">
    <div className="LStudio__app_container_sv_head">
      <span className="LStudio__app_container_sv_eyebrow">QUÉ HACEMOS</span>
      <h2 className="LStudio__app_content_sv_heading">Servicios</h2>
      <p className="LStudio__app_content_sv_subtitle">Creamos software, soluciones web y mobile, agentes inteligentes y automatizaciones enfocadas en resolver necesidades reales.</p>
    </div>
    <div className="LStudio__app_container_sv_static_grid">
      {services.map((s,i)=>{
        const Icon=SERVICE_ICONS[s.icon]||FaCode;
        return <div key={`${s.title}-${i}`} className="LStudio__app_container_sv_card" data-index={i+1}>
          <span className="LStudio__app_container_sv_card_grid"/>
          <span className="LStudio__app_container_sv_card_nodes">
            <span className="LStudio__app_content_sv_node LStudio__app_content_sv_node_1"/>
            <span className="LStudio__app_content_sv_node LStudio__app_content_sv_node_2"/>
            <span className="LStudio__app_content_sv_node LStudio__app_content_sv_node_3"/>
          </span>
          <span className="LStudio__app_container_sv_card_corner LStudio__app_container_sv_card_corner_tl"/>
          <span className="LStudio__app_container_sv_card_corner LStudio__app_container_sv_card_corner_br"/>
          <div className="LStudio__app_container_sv_card_visual">
            <span className="LStudio__app_content_sv_visual_ring"/>
            <span className="LStudio__app_content_sv_visual_ring LStudio__app_content_sv_visual_ring_alt"/>
            <Icon className="LStudio__app_content_sv_visual_icon"/>
          </div>
          <h3 className="LStudio__app_container_sv_card_title">{s.title}</h3>
        </div>;
      })}
    </div>
  </section>;
}

function Clients({clients}) {
  if(!clients.length)return null;
  return <section className="LStudio__app_container_home_clients_showcase" id="clientes">
    <div className="LStudio__app_container_clients_showcase_header"><span className="LStudio__app_content_clients_showcase_eyebrow">// CONFIANZA</span><h2 className="LStudio__app_content_clients_showcase_title">Con quién trabajamos.</h2><p className="LStudio__app_content_clients_showcase_subtitle">Empresas de distintos rubros que ya confiaron su producto a Loopy Studios.</p></div>
    <div className="LStudio__app_container_clients_showcase_static_row">{clients.map((c,i)=>{
      const externalUrl = getExternalUrl(c);
      return <div key={`${c.name}-${i}`} className="LStudio__app_content_clients_showcase_static_item">
        <div
          className={`LStudio__app_container_clients_showcase_card ${externalUrl ? 'is-clickable' : ''}`.trim()}
          onClick={()=>openExternalUrl(c)}
          onKeyDown={(e)=>{if(externalUrl && (e.key==='Enter'||e.key===' ')){e.preventDefault();openExternalUrl(c)}}}
          role={externalUrl?'link':undefined}
          tabIndex={externalUrl?0:undefined}
          aria-label={externalUrl?`Abrir sitio de ${c.name}`:undefined}
        >
          <div className="LStudio__app_container_clients_showcase_logo">{c.logo?<img src={resolveDbAsset(c.logo)} alt={c.name} className="LStudio__app_content_clients_showcase_logo_image" onError={e=>{e.currentTarget.style.display='none';e.currentTarget.nextSibling.style.display='grid'}}/>:null}<span style={{display:c.logo?'none':'grid'}} className="LStudio__app_content_clients_showcase_logo_fallback_icon"><FaBuilding/></span></div>
          <div className="LStudio__app_container_clients_showcase_card_footer"><span className="LStudio__app_container_clients_showcase_card_name">{c.name}</span><span className="LStudio__app_container_clients_showcase_card_category">{c.industry||c.category}</span></div>
        </div>
      </div>
    })}</div>
  </section>
}

function Projects({projects}) {
  return <section id="projects" className="LStudio__app_container_ps_wrapper LoopyLegacyProjects">
    <div className="ps-section-head LoopyLegacyProjects__head">
      <span className="ps-eyebrow">// TRABAJOS</span>
      <h2>Proyectos</h2>
    </div>

    <Swiper
      modules={[Navigation, Pagination]}
      className="LoopyLegacyProjects__swiper"
      spaceBetween={18}
      slidesPerView={1.06}
      speed={700}
      navigation={{ prevEl: '.LoopyLegacyProjects__prev', nextEl: '.LoopyLegacyProjects__next' }}
      pagination={{ clickable: true }}
      breakpoints={{ 760: { slidesPerView: 1.55 }, 1100: { slidesPerView: 2.15 } }}
    >
      {projects.map((project, index) => (
        <SwiperSlide key={`${project.title}-${index}`}>
          <article
            className={`LoopyLegacyProjects__card ${getExternalUrl(project) ? 'is-clickable' : ''}`.trim()}
            onClick={()=>openExternalUrl(project)}
            onKeyDown={(e)=>{if(getExternalUrl(project) && (e.key==='Enter'||e.key===' ')){e.preventDefault();openExternalUrl(project)}}}
            role={getExternalUrl(project)?'link':undefined}
            tabIndex={getExternalUrl(project)?0:undefined}
            aria-label={getExternalUrl(project)?`Abrir ${project.title}`:undefined}
          >
            <div className="LoopyLegacyProjects__imageWrap">
              <img src={resolveDbAsset(project.image || project.thumbnail)} alt={project.title} loading="lazy" />
            </div>
            <div className="LoopyLegacyProjects__body">
              <span className="LoopyLegacyProjects__category">{CATEGORY_LABEL[project.category] || project.category}</span>
              <h3>{project.title}</h3>
              <p>{project.text || project.description}</p>
              {project.stack && <div className="LoopyLegacyProjects__meta"><span>{project.stack}</span></div>}
            </div>
          </article>
        </SwiperSlide>
      ))}
    </Swiper>

    <div className="LoopyLegacyProjects__nav" aria-label="Navegación de proyectos">
      <button type="button" className="LoopyLegacyProjects__navButton LoopyLegacyProjects__prev" aria-label="Proyecto anterior"><FaArrowLeft /></button>
      <button type="button" className="LoopyLegacyProjects__navButton LoopyLegacyProjects__next" aria-label="Siguiente proyecto"><FaArrowRight /></button>
    </div>
  </section>;
}

function About({ onOpenContact }) { return <><section id="about" className="LStudio__app_container_ab_section"><div className="LStudio__app_container_ab_wrap"><div className="ab-content"><span className="LStudio__app_container_ab_eyebrow">CÓMO TRABAJAMOS</span><h2 className="LStudio__app_content_ab_heading">Somos Loopy Studios</h2><p className="LStudio__app_content_ab_text">Diseñamos y construimos soluciones digitales con criterio, comunicación directa y una obsesión simple: que cada entrega tenga sentido, calidad y personalidad.</p><div className="LStudio__app_container_ab_signature"><span className="LStudio__app_content_ab_since">DESDE 2025</span><strong className="LStudio__app_content_ab_motto">Marcamos la diferencia buscando destacar en cada entrega.</strong><p className="LStudio__app_content_ab_method">Nos involucramos de verdad: entendemos el problema, proponemos con criterio y construimos con comunicación directa, detalle y responsabilidad.</p></div><span className="LStudio__app_container_ab_location"><span className="LStudio__app_content_ab_location_dot"/>La Plata, Buenos Aires, Argentina</span></div><div className="LStudio__app_container_ab_map_frame"><iframe title="Loopy Studios - Plaza Moreno, La Plata" src="https://www.google.com/maps?q=Plaza+Moreno,+La+Plata,+Buenos+Aires,+Argentina&z=16&output=embed" loading="lazy"/><span className="LStudio__app_container_ab_map_grid"/><span className="LStudio__app_content_ab_map_vignette"/></div></div></section>
 <section className="LStudio__app_container_team_section"><div className="LStudio__app_container_team_grid"><div className="LStudio__app_container_team_visual"><img className="LStudio__app_content_team_photo" src={HERO_IMAGE} alt="Equipo de Loopy Studios"/><figure className="LStudio__app_container_team_quote"><span className="LStudio__app_container_team_quote_grid"/><blockquote className="LStudio__app_content_team_quote_text">Detrás de cada entrega hay personas que piensan, proponen y se hacen cargo. Trabajamos cerca, hablamos claro y construimos en equipo.</blockquote><figcaption className="LStudio__app_content_team_quote_author">Loopy Studios</figcaption></figure></div><div className="LStudio__app_container_team_copy"><span className="LStudio__app_content_team_eyebrow">NUESTRO EQUIPO</span><h2 className="LStudio__app_content_team_heading">Somos Loopiers</h2><p className="LStudio__app_content_team_text">Somos personas con perfiles distintos que comparten la misma forma de trabajar: involucrarnos, proponer y hacernos cargo.</p><button type="button" className="LStudio__app_content_team_link LoopyContactTrigger" onClick={onOpenContact}><span className="LStudio__app_content_team_link_icon"><FaArrowRight/></span>Hablemos</button></div></div></section></> }

function LoopyLegacyInfinityLoader({ compact = false }) {
  return (
    <div className={`LSLandingBoot__infinityWrap ${compact ? 'LSLandingBoot__infinityWrap--compact' : ''}`.trim()} role="status" aria-label="Cargando">
      <svg className="LSLandingBoot__infinity" viewBox="0 0 100 50" width={compact ? 42 : 104} height={compact ? 22 : 52} aria-hidden="true">
        <path d="M 20,25 C 20,10 40,10 50,25 C 60,40 80,40 80,25 C 80,10 60,10 50,25 C 40,40 20,40 20,25 Z" fill="none" strokeLinecap="round" />
      </svg>
      {!compact && <span>Cargando Loopy Studios…</span>}
    </div>
  );
}

function FloatingContact({ open, onOpen, onClose }) {
  const noAutofill = useNoAutofill({ hard: true });
  const SERVICE_OPTIONS = ['Software a Medida & SaaS','Desarrollo Web & Mobile','Agentes IA','Inteligencia Artificial','Automatización'];
  const BUDGET_OPTIONS = ['A definir','Menos de $100 USD','Menos de $300 USD','Menos de $700 USD','Menos de $1.000 USD','$1.000 - $5.000 USD','Más de $5.000 USD'];
  const openedAtRef = useRef(Date.now());
  const [captcha, setCaptcha] = useState(() => ({ a: Math.floor(Math.random() * 8) + 2, b: Math.floor(Math.random() * 7) + 1 }));
  const [form, setForm] = useState({ name:'', email:'', service:'', budget:'A definir', message:'', captcha:'', website:'' });
  const [touched, setTouched] = useState({});
  const [openSelect, setOpenSelect] = useState(null);
  const [submitState, setSubmitState] = useState({ status:'idle', message:'' });

  useEffect(() => {
    if (open) openedAtRef.current = Date.now();
  }, [open]);

  const refreshCaptcha = () => {
    setCaptcha({ a: Math.floor(Math.random() * 8) + 2, b: Math.floor(Math.random() * 7) + 1 });
    setForm(v => ({ ...v, captcha:'' }));
    setTouched(v => ({ ...v, captcha:false }));
  };

  const setField = (name, value) => setForm(v => ({ ...v, [name]: value }));
  const markTouched = (name) => setTouched(v => ({ ...v, [name]: true }));
  const nameError = validateContactName(form.name);
  const emailError = validateContactEmail(form.email);
  const messageError = validateContactMessage(form.message);
  const captchaOk = form.captcha !== '' && Number(form.captcha) === captcha.a + captcha.b;
  const errors = {
    name: nameError,
    email: emailError,
    service: !SERVICE_OPTIONS.includes(form.service) ? 'Seleccioná un servicio válido.' : '',
    budget: !BUDGET_OPTIONS.includes(form.budget) ? 'Seleccioná un presupuesto válido.' : '',
    message: messageError,
    captcha: !form.captcha ? 'Resolvé la operación anti-spam.' : (!captchaOk ? 'El resultado no es correcto.' : ''),
  };
  const canSubmit = !errors.name && !errors.email && !errors.service && !errors.budget && !errors.message && !errors.captcha;

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ name:true, email:true, service:true, budget:true, message:true, captcha:true });
    setSubmitState({ status:'idle', message:'' });

    // Honeypot: un usuario real nunca debería completar este campo invisible.
    if (form.website.trim()) {
      setSubmitState({ status:'error', message:'No pudimos validar el formulario.' });
      return;
    }
    if (!canSubmit) return;

    // Los bots suelen enviar el formulario de inmediato. Este control es adicional y no reemplaza el rate-limit del servidor.
    if (Date.now() - openedAtRef.current < 2500) {
      setSubmitState({ status:'error', message:'Esperá un momento y revisá el formulario antes de enviarlo.' });
      refreshCaptcha();
      return;
    }

    const lastSubmitAt = Number(window.sessionStorage.getItem('loopy-contact-last-submit') || 0);
    if (lastSubmitAt && Date.now() - lastSubmitAt < 15000) {
      setSubmitState({ status:'error', message:'Esperá unos segundos antes de enviar otra consulta.' });
      return;
    }

    setSubmitState({ status:'sending', message:'' });
    const submitStartedAt = Date.now();
    const waitForMinimumLoading = async () => {
      const elapsed = Date.now() - submitStartedAt;
      const remaining = Math.max(0, 2000 - elapsed);
      if (remaining > 0) await new Promise(resolve => window.setTimeout(resolve, remaining));
    };

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 12000);

    try {
      const response = await fetch('/api/contact.php', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          name: normalizeContactText(form.name),
          email: normalizeContactText(form.email).toLowerCase(),
          service: form.service,
          budget: form.budget,
          message: normalizeContactText(form.message),
          website: '',
          captchaA: captcha.a,
          captchaB: captcha.b,
          captchaResult: form.captcha
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok !== true) throw new Error(data?.message || 'No pudimos enviar la consulta. Revisá los datos e intentá nuevamente.');

      // Mantiene visible el spinner Loopy durante un mínimo de 2 segundos.
      // Si el servidor tarda más, no agrega demora adicional.
      await waitForMinimumLoading();

      window.sessionStorage.setItem('loopy-contact-last-submit', String(Date.now()));
      setSubmitState({ status:'success', message:'¡Consulta enviada! Te responderemos a la brevedad.' });
      window.setTimeout(() => {
        setSubmitState(current => current.status === 'success' ? { status:'idle', message:'' } : current);
      }, 3000);
      setForm({ name:'', email:'', service:'', budget:'A definir', message:'', captcha:'', website:'' });
      setTouched({});
      setOpenSelect(null);
      refreshCaptcha();
      openedAtRef.current = Date.now();
    } catch (error) {
      // También mantenemos el feedback visual mínimo cuando el servidor rechaza rápido.
      await waitForMinimumLoading();
      const message = error?.name === 'AbortError'
        ? 'La solicitud tardó demasiado. Intentá nuevamente.'
        : (error?.message || 'No pudimos enviar la consulta. Intentá nuevamente.');
      setSubmitState({ status:'error', message });
      refreshCaptcha();
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const CustomSelect = ({ name, value, options, placeholder, requiredField=false }) => {
    const isOpen = openSelect === name;
    const choose = (option) => {
      if (!options.includes(option)) return;
      setField(name, option);
      markTouched(name);
      setOpenSelect(null);
    };
    return <div className={`LoopyCustomSelect ${isOpen ? 'is-open' : ''} ${touched[name] && errors[name] ? 'has-error' : ''}`}>
      <button type="button" className="LoopyCustomSelect__control" onClick={()=>setOpenSelect(isOpen ? null : name)} aria-haspopup="listbox" aria-expanded={isOpen}>
        <span className={!value ? 'is-placeholder' : ''}>{value || placeholder}</span><FaChevronDown aria-hidden="true"/>
      </button>
      {isOpen && <div className="LoopyCustomSelect__menu" role="listbox">
        {options.map(option => <button type="button" key={option} role="option" aria-selected={value===option} className={value===option ? 'is-selected' : ''} onClick={()=>choose(option)}>{option}</button>)}
      </div>}
      {requiredField && touched[name] && errors[name] && <div className="LoopyFloatingContact__fieldError">{errors[name]}</div>}
    </div>;
  };

  return <div className={`LoopyFloatingContact ${open ? 'is-open' : ''}`}>
    <button type="button" className="LoopyFloatingContact__button" onClick={open ? onClose : onOpen} aria-label={open ? 'Cerrar consulta' : 'Crear consulta'} aria-expanded={open} title={open ? 'Cerrar consulta' : 'Crear consulta'}><FaComments/></button>
    <div className="LoopyFloatingContact__panel" aria-hidden={!open}>
      <div className="LoopyFloatingContact__head"><div><span>// CONTACTO</span><strong>Contanos tu proyecto</strong></div><button type="button" onClick={onClose} aria-label="Cerrar consulta"><FaTimes/></button></div>
      <form onSubmit={submit} autoComplete="off" noValidate>
        <input className="LoopyFloatingContact__honeypot" type="text" {...noAutofill('website')} value={form.website} onChange={e=>setField('website',e.target.value.slice(0,120))} tabIndex="-1" aria-hidden="true" />
        <div className="LoopyFloatingContact__grid">
          <label><span>Nombre <b className="LoopyFloatingContact__required">*</b></span><input {...noAutofill('name')} value={form.name} maxLength={CONTACT_FORM_LIMITS.nameMax} onChange={e=>setField('name',e.target.value)} onBlur={()=>markTouched('name')} placeholder="¿Cómo te llamás?" className={touched.name && errors.name ? 'has-error' : ''} />{touched.name && errors.name && <small className="LoopyFloatingContact__fieldError">{errors.name}</small>}</label>
          <label><span>Email <b className="LoopyFloatingContact__required">*</b></span><input {...noAutofill('email')} type="email" inputMode="email" maxLength={CONTACT_FORM_LIMITS.emailMax} value={form.email} onChange={e=>setField('email',e.target.value)} onBlur={()=>markTouched('email')} placeholder="nombre@empresa.com" className={touched.email && errors.email ? 'has-error' : ''} />{touched.email && errors.email && <small className="LoopyFloatingContact__fieldError">{errors.email}</small>}</label>
        </div>
        <label><span>Servicio <b className="LoopyFloatingContact__required">*</b></span><CustomSelect name="service" value={form.service} options={SERVICE_OPTIONS} placeholder="Elegí una opción" requiredField /></label>
        <label><span>Presupuesto</span><CustomSelect name="budget" value={form.budget} options={BUDGET_OPTIONS} placeholder="A definir" /></label>
        <label><span>Mensaje <b className="LoopyFloatingContact__required">*</b></span><textarea {...noAutofill('message')} rows="5" maxLength={CONTACT_FORM_LIMITS.messageMax} value={form.message} onChange={e=>setField('message',e.target.value)} onBlur={()=>markTouched('message')} placeholder="¿Qué querés lograr?" className={touched.message && errors.message ? 'has-error' : ''} />{touched.message && errors.message && <small className="LoopyFloatingContact__fieldError">{errors.message}</small>}</label>
        <div className={`LoopyFloatingContact__captcha ${touched.captcha && errors.captcha ? 'has-error' : ''}`}>
          <div><span>ANTI-SPAM <b className="LoopyFloatingContact__required">*</b></span><strong>{captcha.a} + {captcha.b} = ?</strong></div>
          <input {...noAutofill('captcha')} type="text" inputMode="numeric" value={form.captcha} onChange={e=>setField('captcha', e.target.value.replace(/\D/g,'').slice(0,3))} onBlur={()=>markTouched('captcha')} maxLength={3} placeholder="Resultado" aria-label={`Resultado de ${captcha.a} más ${captcha.b}`} className={touched.captcha && errors.captcha ? 'has-error' : ''} />
        </div>
        {touched.captcha && errors.captcha && <div className="LoopyFloatingContact__fieldError LoopyFloatingContact__fieldError--captcha">{errors.captcha}</div>}
        {submitState.message && <div className={`LoopyFloatingContact__submitStatus is-${submitState.status}`} role="status">{submitState.message}</div>}
        <button
          type="submit"
          className={`LoopyFloatingContact__submit ${submitState.status==='sending' ? 'is-sending' : ''}`.trim()}
          disabled={!canSubmit || submitState.status==='sending'}
          aria-busy={submitState.status==='sending'}
        >
          {submitState.status==='sending' ? <LoopyLegacyInfinityLoader compact /> : <>Enviar consulta <FaPaperPlane/></>}
        </button>
      </form>
    </div>
  </div>;
}

export const LoopyLandingPage = () => {
  const [services,setServices]=useState(FALLBACK_SERVICES), [projects,setProjects]=useState(FALLBACK_PROJECTS), [clients,setClients]=useState(FALLBACK_CLIENTS), [showTop,setShowTop]=useState(false), [showContact,setShowContact]=useState(false), [contactOpen,setContactOpen]=useState(false);
  useEffect(()=>{let active=true;const load=async(path,fallback)=>{try{const r=await fetch(`${path}?v=${Date.now()}`,{cache:'no-store'});if(!r.ok)throw new Error();const d=await r.json();return Array.isArray(d)?d:fallback}catch{return fallback}};(async()=>{const [s,p,c]=await Promise.all([load('/DB_LOCAL/Services.json',FALLBACK_SERVICES),load('/DB_LOCAL/Projects.json',FALLBACK_PROJECTS),load('/DB_LOCAL/Clients.json',FALLBACK_CLIENTS)]);if(!active)return;setServices(s);setProjects(p);setClients(c.filter((x,i,a)=>a.findIndex(y=>y.name===x.name)===i));})();return()=>{active=false}},[]);
  useEffect(()=>{const h=()=>{const hero=document.querySelector('.LStudio__app_container_hero_view');const pastHero=hero ? hero.getBoundingClientRect().bottom<=130 : window.scrollY>window.innerHeight*.85;setShowTop(window.scrollY>window.innerHeight*.9);setShowContact(pastHero)};window.addEventListener('scroll',h,{passive:true});h();return()=>window.removeEventListener('scroll',h)},[]);
  return <div id="home" className="LoopyLandingLegacy"><LandingHeader/><main className="LoopyLandingLegacy__main"><Hero/><Services services={services}/><Clients clients={clients}/><Projects projects={projects}/><About onOpenContact={()=>setContactOpen(true)}/></main><LandingFooter/>{(showContact || contactOpen) && <FloatingContact open={contactOpen} onOpen={()=>setContactOpen(true)} onClose={()=>setContactOpen(false)}/>} {showTop&&<button className="LStudio__app_content_scroll_top" aria-label="Volver arriba" onClick={()=>window.scrollTo({top:0,behavior:'smooth'})}><FaArrowUp/></button>}</div>;
};
